import { useState, useContext } from 'react';
import { Container } from '@mui/material';
import { motion } from 'framer-motion';
import UserContext from '../../../../context/userContext';
import style from './HRPage.module.css';

/* --------------- mock data --------------- */
const saldo = {
    ferie:       { remaining: 17, total: 25 },
    egenmelding: { remaining: 9,  total: 12 },
    sykefravaer: { days: 3 },
};

type HistoryEntry =
    | { type: 'ferie'; status: 'godkjent' | 'venter' | 'avslatt'; date: string; range: string; days: number; by: string }
    | { type: 'egenmelding'; status: 'registrert'; date: string; days: number };

const history: HistoryEntry[] = [
    { type: 'ferie',       status: 'godkjent',   date: '23.04.2026', range: '01.05 – 05.05', days: 5,  by: 'Ola Hansen' },
    { type: 'egenmelding', status: 'registrert', date: '12.04.2026', days: 1 },
    { type: 'ferie',       status: 'venter',     date: '28.04.2026', range: '14.07 – 25.07', days: 10, by: 'Ola Hansen' },
    { type: 'egenmelding', status: 'registrert', date: '03.02.2026', days: 2 },
    { type: 'ferie',       status: 'godkjent',   date: '15.12.2025', range: '12.01 – 14.01', days: 3,  by: 'Ola Hansen' },
];

function workDaysBetween(from: Date, to: Date): number {
    if (isNaN(from.getTime()) || isNaN(to.getTime()) || from > to) return 0;
    let count = 0;
    const cur = new Date(from);
    while (cur <= to) {
        const d = cur.getDay();
        if (d !== 0 && d !== 6) count++;
        cur.setDate(cur.getDate() + 1);
    }
    return count;
}

/* --------------- Feriesøknad --------------- */
function FerieCard() {
    const { setSnackbarText } = useContext(UserContext);
    const [from, setFrom] = useState('2026-07-14');
    const [to, setTo] = useState('2026-07-25');
    const [comment, setComment] = useState('');

    const days = workDaysBetween(new Date(from), new Date(to));
    const remaining = saldo.ferie.remaining - days;
    const tooMany = days > saldo.ferie.remaining;

    return (
        <article className={style.actionCard}>
            <header className={style.actionHead}>
                <span className={style.actionKicker}>Søknad</span>
                <h2 className={style.actionTitle}>Feriesøknad</h2>
            </header>

            <div className={style.formGrid}>
                <label className={style.field}>
                    <span className={style.fieldLabel}>Fra</span>
                    <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className={style.input} />
                </label>
                <label className={style.field}>
                    <span className={style.fieldLabel}>Til</span>
                    <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className={style.input} />
                </label>
            </div>

            <div className={style.computedRow}>
                <div className={style.computed}>
                    <span className={style.compLabel}>Hverdager</span>
                    <span className={style.compValue}>{days}</span>
                </div>
                <div className={style.computed}>
                    <span className={style.compLabel}>Saldo etter</span>
                    <span className={`${style.compValue} ${tooMany ? style.compValueWarn : ''}`}>
                        {Math.max(0, remaining)}<span className={style.compTotal}>&thinsp;/ {saldo.ferie.total}</span>
                    </span>
                </div>
            </div>

            <label className={style.field}>
                <span className={style.fieldLabel}>Kommentar til leder (valgfritt)</span>
                <textarea
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    className={style.textarea}
                    rows={2}
                    placeholder="Reise, livsstilen, eller bare en notat…"
                />
            </label>

            <div className={style.approver}>
                Godkjennes av <strong>Ola Hansen</strong> · Avdelingsleder
            </div>

            <button
                className={style.submitBtn}
                disabled={days <= 0 || tooMany}
                onClick={() => setSnackbarText('Feriesøknad sendt — venter på Ola Hansen')}
            >
                Send søknad
                <span className={style.btnArrow} aria-hidden="true">→</span>
            </button>
        </article>
    );
}

/* --------------- Egenmelding --------------- */
function EgenmeldingCard() {
    const { setSnackbarText } = useContext(UserContext);
    const today = new Date().toISOString().slice(0, 10);
    const [from, setFrom] = useState(today);
    const [to, setTo] = useState(today);
    const [confirmFit, setConfirmFit] = useState(false);

    const days = workDaysBetween(new Date(from), new Date(to));
    const remaining = saldo.egenmelding.remaining - days;
    const tooMany = days > saldo.egenmelding.remaining;
    const tooLong = days > 3;

    return (
        <article className={style.actionCard}>
            <header className={style.actionHead}>
                <span className={style.actionKicker}>Selvrapportering</span>
                <h2 className={style.actionTitle}>Egenmelding</h2>
            </header>

            <div className={style.formGrid}>
                <label className={style.field}>
                    <span className={style.fieldLabel}>Fra</span>
                    <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className={style.input} />
                </label>
                <label className={style.field}>
                    <span className={style.fieldLabel}>Til</span>
                    <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className={style.input} />
                </label>
            </div>

            <div className={style.computedRow}>
                <div className={style.computed}>
                    <span className={style.compLabel}>Dager</span>
                    <span className={`${style.compValue} ${tooLong ? style.compValueWarn : ''}`}>{days}</span>
                </div>
                <div className={style.computed}>
                    <span className={style.compLabel}>Igjen i år</span>
                    <span className={`${style.compValue} ${tooMany ? style.compValueWarn : ''}`}>
                        {Math.max(0, remaining)}<span className={style.compTotal}>&thinsp;/ {saldo.egenmelding.total}</span>
                    </span>
                </div>
            </div>

            <label className={style.checkRow}>
                <input
                    type="checkbox"
                    checked={confirmFit}
                    onChange={(e) => setConfirmFit(e.target.checked)}
                    className={style.checkbox}
                />
                <span>Jeg bekrefter at jeg ikke er arbeidsfør i perioden.</span>
            </label>

            {tooLong && (
                <div className={style.warning}>
                    Egenmelding kan vare i inntil 3 sammenhengende kalenderdager. Trenger du mer, må du levere sykmelding.
                </div>
            )}

            <button
                className={style.submitBtn}
                disabled={days <= 0 || tooMany || tooLong || !confirmFit}
                onClick={() => setSnackbarText('Egenmelding registrert')}
            >
                Registrer
                <span className={style.btnArrow} aria-hidden="true">→</span>
            </button>
        </article>
    );
}

/* --------------- Page --------------- */
export default function HRPage() {
    return (
        <Container maxWidth={false} className={style.page}>
            <motion.header
                className={style.pageHead}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.45 }}
            >
                <h1 className={style.pageTitle}>HR</h1>
            </motion.header>

            <div className={style.headRule} />

            {/* Saldo strip */}
            <section className={style.saldoStrip}>
                <div className={style.saldoBlock}>
                    <div className={style.saldoLabel}>Ferie igjen</div>
                    <div className={style.saldoValue}>
                        <span className={style.saldoBig}>{saldo.ferie.remaining}</span>
                        <span className={style.saldoTotal}>av {saldo.ferie.total} dager</span>
                    </div>
                    <div className={style.saldoBar}>
                        <div className={style.saldoFill} style={{ width: `${(saldo.ferie.remaining / saldo.ferie.total) * 100}%` }} />
                    </div>
                    <div className={style.saldoFoot}>{saldo.ferie.total - saldo.ferie.remaining} dager brukt i 2026</div>
                </div>
                <div className={style.saldoBlock}>
                    <div className={style.saldoLabel}>Egenmelding</div>
                    <div className={style.saldoValue}>
                        <span className={style.saldoBig}>{saldo.egenmelding.remaining}</span>
                        <span className={style.saldoTotal}>av {saldo.egenmelding.total} dager</span>
                    </div>
                    <div className={style.saldoBar}>
                        <div className={`${style.saldoFill} ${style.saldoFillGold}`} style={{ width: `${(saldo.egenmelding.remaining / saldo.egenmelding.total) * 100}%` }} />
                    </div>
                    <div className={style.saldoFoot}>3 av 4 perioder gjenstår</div>
                </div>
                <div className={style.saldoBlock}>
                    <div className={style.saldoLabel}>Sykefravær</div>
                    <div className={style.saldoValue}>
                        <span className={style.saldoBig}>{saldo.sykefravaer.days}</span>
                        <span className={style.saldoTotal}>dager hittil i 2026</span>
                    </div>
                    <div className={style.saldoBar}>
                        <div className={`${style.saldoFill} ${style.saldoFillSoft}`} style={{ width: `${(saldo.sykefravaer.days / 30) * 100}%` }} />
                    </div>
                    <div className={style.saldoFoot}>Brynild-snitt: 4,2 dager</div>
                </div>
            </section>

            {/* Action cards */}
            <section className={style.actions}>
                <FerieCard />
                <EgenmeldingCard />
            </section>

            {/* History */}
            <section className={style.section}>
                <header className={style.sectionHead}>
                    <span className={style.sectionKicker}>Logg</span>
                    <h2 className={style.sectionTitle}>Mine forespørsler</h2>
                    <span className={style.sectionMeta}>5 av 18 viste</span>
                </header>
                <div className={style.historyList}>
                    {history.map((it, i) => (
                        <motion.div
                            key={i}
                            className={style.historyItem}
                            initial={{ opacity: 0, x: -4 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.35, delay: 0.05 + i * 0.04 }}
                        >
                            <div className={style.historyDate}>{it.date}</div>
                            <div className={`${style.historyType} ${style[`type_${it.type}`]}`}>
                                {it.type === 'ferie' ? 'Ferie' : 'Egenmelding'}
                            </div>
                            <div className={style.historyBody}>
                                {it.type === 'ferie' ? (
                                    <>
                                        <span className={style.historyTitle}>{it.range}</span>
                                        <span className={style.historyMeta}>{it.days} hverdager · godkjenner: {it.by}</span>
                                    </>
                                ) : (
                                    <>
                                        <span className={style.historyTitle}>Egen sykdom</span>
                                        <span className={style.historyMeta}>{it.days} {it.days === 1 ? 'dag' : 'dager'}</span>
                                    </>
                                )}
                            </div>
                            <div className={`${style.historyStatus} ${style[`status_${it.status}`]}`}>
                                {it.status === 'godkjent'   && 'Godkjent'}
                                {it.status === 'venter'     && 'Venter'}
                                {it.status === 'registrert' && 'Registrert'}
                                {it.status === 'avslatt'    && 'Avslått'}
                            </div>
                        </motion.div>
                    ))}
                </div>
            </section>
        </Container>
    );
}
